// D-1124: 「通信が途切れた・圏外なだけ」のエラーかを見分ける(判定はここ 1 か所)。
//   アプリの不具合ではなく現場の電波の話なので、logger.error はこれに当たると Sentry に例外で送らず breadcrumb にする。
//   参考: https://docs.sentry.io/platforms/javascript/configuration/filtering/
//
// ★依存を持たない純関数のモジュール。offlineGate は logger を import しているので、ここから offlineGate を
//   import すると logger → networkError → offlineGate → logger の循環になる。守衛の文言と code は値で持ち、
//   一致はテスト(loggerNetworkErrorD1124.test.js が守衛の実際の 503 本文を通す)で固定する。
//
// 既存の似た判定はまとめていない(挙動が変わるため):
//   - services/appFeedback.js の isNetworkError: 画面に「圏外」と出すための判定で、HTTP 503/0 と /network/i まで広く拾う。
//   - services/storeWeather.js: 例外の name(AbortError 等)で文言を分けるだけで、文字列の判定は無い。
//   - collection/lib/collectionPdf.js: フォント取得の失敗を握りつぶすだけで、判定は無い(コメントに Load failed があるのみ)。

// ★errorCodes.js は import を持たない定数だけのモジュールなので、ここから import しても循環にならない。
import { ERR } from './errorCodes'

// D-1124 追補: ど安定 5 点(メーター記録・集金根拠・景品在庫・認証)の保存・送信の失敗のイベント名(一覧はここ 1 か所)。
//   これらは通信断・圏外でも今までどおり Sentry に captureException する(ど安定 5 点の監視の挙動は変えない)。
//   logger.error は event がこの一覧にあれば、通信断の判定より先に従来の経路へ回す。
//   ★呼び出し元のファイルは変えない。名前は呼び出し元の文字列そのまま(テストが呼び出し元のソースと突き合わせて固定する)。
//   入れていないもの: LF1_UPLOAD_DONE / LF1_COLLECTION_FLUSH_DONE(payload は件数だけで判定に当たらない)、
//   ERR-LF1-IDB-*(IndexedDB のエラーで fetch の文言にならない)、ERR-AUTH-001(固定の文言)、読み込みだけの失敗。
export const ALWAYS_CAPTURE_EVENTS = new Set([
  // メーター記録
  ERR.METER_001,                       // 'ERR-METER-001'  services/patrolCore.js:598, 623
  ERR.METER_002,                       // 'ERR-METER-002'  services/patrolCore.js:505
  'patrol_save_failed_unexpected',     // services/patrolCore.js:630 / clawsupport/pages/PatrolBoothInputPage.jsx:1348
  'ERR-LF1-SYNC-UPLOAD',               // services/storeSync.js:169, 176, 188, 197(LOG_ERR_UPLOAD)
  ERR.IMPORT_003,                      // 'ERR-IMPORT-003' admin/lib/bulkInsertMeterReadings.js:56(meter_readings の一括 INSERT)
  // 集金根拠
  'ERR-LF1-COLLECTION-SEND',           // services/collectionSync.js:135, 143(LOG_ERR_SEND)
  'collection_receipt_upload_failed',  // admin/pages/AdminCollectionBoothEditPage.jsx:142
  'collection_receipt_save_failed',    // admin/pages/AdminCollectionBoothEditPage.jsx:149
  'collection_advance_save_failed',    // admin/pages/AdminCollectionBoothEditPage.jsx:159
  // 景品在庫
  'stocktake_save_failed',             // tanasupport/stocktake/saveSafety.js:72(D-1016 で本番でも送ると決めたもの)
  'stock_movement_record_failed',      // clawsupport/pages/PatrolBoothInputPage.jsx:1179
  'arrival_receive_error',             // tanasupport/components/ArrivalReceiveSheet.jsx:120, 128
  'arrival_reverse_error',             // tanasupport/components/ArrivalReverseSheet.jsx:105, 113
  'stock_out_error',                   // tanasupport/pages/StockOutPage.jsx:132, 150
  'arrival_staff_receive_error',       // services/arrivalReceipts.js:283, 333
  'arrival_staff_undo_error',          // services/arrivalReceipts.js:371
  'ERR-PRIZE-SET-JAN',                 // services/prizeJan.js:42, 56(prize_masters の JAN 書き込み)
  'ERR-PRIZE-PROVISIONAL',             // services/prizeProvisional.js:121, 147(仮景品の作成)
  'ERR-PRIZE-PROVISIONAL-COST',        // services/prizeProvisional.js:203, 213(仮景品の単価の書き込み)
  // 認証
  'staff_pin_set_error',               // admin/pages/AdminStaffListPage.jsx:454(PIN の設定)
])

/** 通信断・圏外でも必ず captureException するイベントか(ALWAYS_CAPTURE_EVENTS)。 */
export function isAlwaysCaptureEvent(event) {
  return ALWAYS_CAPTURE_EVENTS.has(event)
}

// offlineGate.js の守衛が書き込みを止めたときの 503 本文(message / code)。
export const OFFLINE_GATE_MESSAGE = '圏外継続中のため通信しません'
export const OFFLINE_GATE_CODE = 'OFFLINE_CONTINUATION'

// breadcrumb の category。Sentry で後から本物のエラーの文脈として「直前に通信断があった」と分かるようにする。
export const NETWORK_BREADCRUMB_CATEGORY = 'network.lost'

// ブラウザの fetch がネット断で投げる TypeError の文言。supabase-js は「TypeError: Load failed (host)」の形で error.message に入れる。
//   Safari: Load failed / The network connection was lost.   Chrome: Failed to fetch   Firefox: NetworkError when attempting to fetch resource.
//   大文字小文字は区別する(アプリ内の「load failed」等の自前の文言を拾わない)。
//   「Failed to fetch dynamically imported module」は古いチャンクの読み込み失敗(デプロイ跨ぎ)なので除く。
const NETWORK_PATTERNS = [
  /\bLoad failed\b/,
  /\bFailed to fetch\b(?! dynamically imported module)/,
  /\bNetworkError when attempting to fetch resource\b/,
  /\bThe network connection was lost\b/,
]

function messageOf(v) {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object' && typeof v.message === 'string') return v.message
  return ''
}

function isNetworkMessage(msg) {
  if (!msg) return false
  if (msg.includes(OFFLINE_GATE_MESSAGE)) return true
  return NETWORK_PATTERNS.some(re => re.test(msg))
}

/**
 * 通信断・圏外なだけのエラーか。
 * 見る場所: 値そのもの(文字列 / Error)、payload.message、payload.error(文字列 or { message })、payload.raw、payload.code。
 *   logger.error の呼び出し元は { message } / { error } / { raw } / Error をそのまま渡す形が混在しているため。
 * @param {unknown} payload
 * @returns {boolean}
 */
export function isNetworkOnlyError(payload) {
  if (payload == null) return false
  if (typeof payload === 'string') return isNetworkMessage(payload)
  if (typeof payload !== 'object') return false
  if (payload.code === OFFLINE_GATE_CODE) return true
  return [payload, payload.error, payload.raw].some(v => isNetworkMessage(messageOf(v)))
}

/** breadcrumb に残す 1 行の文言(どの値が当たったか)。 */
export function networkErrorMessageOf(payload) {
  if (payload == null) return ''
  if (typeof payload === 'string') return payload
  for (const v of [payload, payload.error, payload.raw]) {
    const m = messageOf(v)
    if (isNetworkMessage(m)) return m
  }
  return payload.code === OFFLINE_GATE_CODE ? OFFLINE_GATE_MESSAGE : ''
}
